// Scene3D - 3D scene wrapper with Canvas, lights, and controls
import React, { RefObject } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stars, Environment } from '@react-three/drei';
import AllCubes from './AllCubes';
import {
  DataRow,
  LayoutType,
  PaletteType,
  ThemeType,
  SortDirection,
  SizeDimension,
  ShapeType,
  ViewSettings,
  Position3D,
} from '../../types/visualization';
import { THEMES } from '../../constants/visualizationThemes';

// Pinned search term type
interface PinnedSearch {
  term: string;
  color: string;
}

interface SceneContentProps {
  allData: DataRow[];
  layout: LayoutType;
  xAxis: string;
  sortColumn: string;
  sortDirection: SortDirection;
  colorColumn: string;
  colorPalette: PaletteType;
  searchQuery: string;
  pinnedSearches?: PinnedSearch[];
  setSelectedData: (data: DataRow | null) => void;
  onShiftClick: (data: DataRow, position: Position3D | null) => void;
  hoveredIndex: number | null;
  setHoveredIndex: (index: number | null) => void;
  viewSettings: ViewSettings;
  controlsRef: RefObject<any>;
  theme: ThemeType;
  scatterXColumn: string;
  scatterYColumn: string;
  searchHighlightColor: string;
  cubeMagnification: number;
  metallicMode: boolean;
  shapeType: ShapeType;
  sizeByColumn: string | null;
  sizeByDimension: SizeDimension;
  maxSizeMultiplier: number;
}

function SceneContent({
  allData,
  layout,
  xAxis,
  sortColumn,
  sortDirection,
  colorColumn,
  colorPalette,
  searchQuery,
  pinnedSearches = [],
  setSelectedData,
  onShiftClick,
  hoveredIndex,
  setHoveredIndex,
  viewSettings,
  controlsRef,
  theme,
  scatterXColumn,
  scatterYColumn,
  searchHighlightColor,
  cubeMagnification,
  metallicMode,
  shapeType,
  sizeByColumn,
  sizeByDimension,
  maxSizeMultiplier,
}: SceneContentProps) {
  const t = THEMES[theme];

  return (
    <>
      <color attach="background" args={[t.background]} />
      {viewSettings.showFog && <fog attach="fog" args={[t.fog, 40, 150]} />}

      <ambientLight intensity={theme === 'light' ? 0.8 : 0.6} />
      <pointLight position={[20, 15, 25]} intensity={theme === 'light' ? 1.5 : 2} color="#ffffff" />
      <pointLight position={[-20, 10, -20]} intensity={theme === 'light' ? 0.8 : 1} color={theme === 'light' ? '#ffffff' : '#4a4a4a'} />
      <directionalLight position={[10, 20, 15]} intensity={theme === 'light' ? 0.8 : 1} color="#ffffff" />

      {metallicMode && <Environment preset="city" />}

      {viewSettings.showStars && theme === 'dark' && (
        <Stars radius={200} depth={100} count={1500} factor={4} fade speed={0.1} />
      )}
      {viewSettings.showGrid && (
        <gridHelper args={[150, 75, t.grid1, t.grid2]} position={[0, -0.01, 0]} />
      )}

      <AllCubes
        rows={allData}
        layout={layout}
        xAxis={xAxis}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        colorColumn={colorColumn}
        colorPalette={colorPalette}
        searchQuery={searchQuery}
        pinnedSearches={pinnedSearches}
        onCubeClick={setSelectedData}
        onDoubleClick={onShiftClick}
        hoveredIndex={hoveredIndex}
        onHover={setHoveredIndex}
        animSpeed={viewSettings.animSpeed}
        scatterXColumn={scatterXColumn}
        scatterYColumn={scatterYColumn}
        theme={theme}
        searchHighlightColor={searchHighlightColor}
        cubeMagnification={cubeMagnification}
        metallicMode={metallicMode}
        shapeType={shapeType}
        sizeByColumn={sizeByColumn}
        sizeByDimension={sizeByDimension}
        maxSizeMultiplier={maxSizeMultiplier}
      />

      <OrbitControls
        ref={controlsRef}
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        autoRotate={viewSettings.autoRotate}
        autoRotateSpeed={viewSettings.rotationSpeed * 0.5}
        minDistance={5}
        maxDistance={200}
        maxPolarAngle={Math.PI / 2.05}
      />

    </>
  );
}

interface Scene3DProps extends SceneContentProps {
  cameraPosition?: [number, number, number];
}

export default function Scene3D({ cameraPosition = [0, 10, 40], ...props }: Scene3DProps) {
  return (
    <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
      <Canvas camera={{ position: cameraPosition, fov: 50 }}>
        <SceneContent {...props} />
      </Canvas>
    </div>
  );
}
